import { useId, useState } from 'react';
import parse from 'html-react-parser';
import DOMPurify from 'isomorphic-dompurify';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import chevronUpSVG from '../../../icons/chevron-up.svg';

/**
 * Valor do widget `richtext` (HtmlSlateWidget): objeto com o HTML em `data`.
 * Aceita string crua também, porque conteúdo semeado via API pode chegar assim.
 */
export type RichTextFieldValue =
  | string
  | {
      data?: string;
      'content-type'?: string;
      encoding?: string;
    };

export type FaqItem = {
  question?: string;
  answer?: RichTextFieldValue;
};

export type FaqData = {
  items?: FaqItem[];
};

type FaqBodyProps = {
  data: FaqData;
  isEditMode?: boolean;
};

const getAnswerHtml = (answer?: RichTextFieldValue): string => {
  if (!answer) return '';
  return typeof answer === 'string' ? answer : answer.data || '';
};

/**
 * Sanitiza antes de injetar: o HTML vem do editor (confiável) mas pode ter sido
 * semeado pela API, então não é seguro por construção.
 */
const renderAnswer = (answer?: RichTextFieldValue) => {
  const html = getAnswerHtml(answer);
  if (!html) return null;
  return parse(DOMPurify.sanitize(html));
};

const FaqBody = ({ data, isEditMode }: FaqBodyProps) => {
  const baseId = useId();
  const [openIndexes, setOpenIndexes] = useState<number[]>([]);
  const items = data.items || [];

  const toggle = (index: number) => {
    setOpenIndexes((current) =>
      current.includes(index)
        ? current.filter((i) => i !== index)
        : [...current, index],
    );
  };

  return (
    <section className="block-faq">
      {items.length > 0 && (
        <ul className="faq__list">
          {items.map((item, index) => {
            const buttonId = `${baseId}-q-${index}`;
            const panelId = `${baseId}-a-${index}`;
            // A numeração é POSICIONAL (index + 1), nunca digitada no campo
            // `question`: cinco respostas citam outras perguntas por número
            // ("consulte a pergunta 6") e o mockup tinha tirado a numeração,
            // deixando as referências órfãs. Decisão de 2026-09-09 com o
            // designer, que sobrepõe o Figma — ver docs/specs/blocks/faq.md §6.3.
            // No editor todos os itens ficam abertos e sem botão: o clique deve
            // selecionar o bloco, não abrir/fechar (block-architecture.md §1).
            const isOpen = isEditMode || openIndexes.includes(index);

            return (
              <li className="faq__item" key={index}>
                <h3 className="faq__question">
                  {isEditMode ? (
                    <span className="faq__trigger faq__trigger--static">
                      <span className="faq__question-main">
                        <span className="faq__number">{index + 1}.</span>
                        <span className="faq__question-text">
                          {item.question}
                        </span>
                      </span>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="faq__trigger"
                      id={buttonId}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => toggle(index)}
                    >
                      <span className="faq__question-main">
                        <span className="faq__number">{index + 1}.</span>
                        <span className="faq__question-text">
                          {item.question}
                        </span>
                      </span>
                      <span className="faq__indicator" aria-hidden="true">
                        <Icon name={chevronUpSVG} size="24px" />
                      </span>
                    </button>
                  )}
                </h3>

                <div
                  className="faq__answer"
                  id={panelId}
                  hidden={!isOpen}
                  {...(isEditMode
                    ? {}
                    : { role: 'region', 'aria-labelledby': buttonId })}
                >
                  {renderAnswer(item.answer)}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default FaqBody;
